/**
 * Game trailers.
 *
 * A trailer is one video file on a game, played on the game page in the same gallery the
 * screenshots are in. It is deliberately not a streaming pipeline: one MP4 or WebM in the
 * public bucket, served by `files.goodbuild.cc` over ranged GETs, which is what the player
 * seeks with. Nothing transcodes, so what is uploaded is what plays, and the size rule below
 * is the only thing standing between a studio and a 4 GB master.
 *
 * Both ends read this file. The uploader checks a picked file against it before spending an
 * upload, and the API checks it again before signing a URL.
 */

import { z } from 'zod';
import { ulidSchema } from './common.js';

/**
 * What a browser will actually play.
 *
 * H.264 in MP4 is the one every webview this app ships in can decode, and it is what every
 * editor exports by default. WebM is here for anything already exported that way rather than
 * as a suggestion: offering more formats than that is offering a studio the chance to upload
 * a file that plays on the machine it was made on and nowhere else.
 */
export const TRAILER_TYPES = ['video/mp4', 'video/webm'] as const;
export const trailerContentTypeSchema = z.enum(TRAILER_TYPES);
export type TrailerContentType = z.infer<typeof trailerContentTypeSchema>;

/** How many trailers one game may carry. Past this the gallery stops opening on a trailer. */
export const MAX_TRAILERS = 4;

/**
 * The largest file that may be uploaded, in bytes.
 *
 * A trailer is a minute or two at a bitrate a browser can stream, which is tens of megabytes.
 * Half a gigabyte is well past that and still well inside what one presigned PUT can carry, so
 * the number is a guard against an uncompressed master rather than a budget.
 */
export const MAX_TRAILER_BYTES = 512 * 1024 * 1024;

/** One trailer on a game, as the page receives it. */
export const gameTrailerSchema = z.object({
  id: ulidSchema,
  url: z.string(),
  contentType: trailerContentTypeSchema,
  /**
   * The still shown before it plays, and its thumbnail in the gallery strip.
   *
   * Null is allowed, and then the player draws its own first frame — which on a trailer that
   * opens on a fade from black is a black rectangle in the strip. The uploader takes a frame
   * from the file itself rather than leaving it null, so this is rarely absent in practice.
   */
  posterUrl: z.string().nullable(),
  /** What this one is, when a game has more than one. "Announcement", "Gameplay". */
  title: z.string().nullable(),
  /** Read off the file when it was picked, so the strip can say how long it is. */
  durationMs: z.number().int().nullable(),
});
export type GameTrailer = z.infer<typeof gameTrailerSchema>;

/** The file is not a trailer this app can take, in a sentence, or null when it is. */
export function trailerProblem(contentType: string, sizeBytes: number): string | null {
  if (!(TRAILER_TYPES as readonly string[]).includes(contentType)) {
    return 'An MP4 or a WebM. Anything else has to be exported again first.';
  }
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return 'That file is empty.';
  }
  if (sizeBytes > MAX_TRAILER_BYTES) {
    const mb = Math.round(MAX_TRAILER_BYTES / (1024 * 1024));
    return `A trailer is at most ${String(mb)} MB. Export it again at a bitrate a browser can stream.`;
  }
  return null;
}
